import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { tmpToken }
  const [code, setCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate("/app");
    } else if (res.requires2fa) {
      setTwoFA({ tmpToken: res.tmpToken });
    } else {
      setError(res.error);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await verify2fa(twoFA.tmpToken, code);
    setSubmitting(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-stone-50 grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-emerald-950 text-white relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/508d46d6-a5df-4630-9d10-2e40da390746/images/7a288df1257c0d28acc8230c4974846117fb89c9e37e36628daeb0249265bffd.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="login-logo-link">
          <div className="h-11 w-11 rounded-lg bg-white flex items-center justify-center p-1">
            <img src="/logo.png" alt="PRC会計" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">PRC会計</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-200">SaaS</div>
          </div>
        </Link>
        <div className="relative space-y-6 max-w-md">
          <h2 className="font-display text-3xl xl:text-4xl leading-tight tracking-tight">
            数字に振り回されない、<br />
            あたらしい会計のかたち。
          </h2>
          <p className="text-sm text-emerald-100 leading-relaxed">
            日本の事業者のためにデザインされた、シンプルで美しい会計プラットフォーム。
            あなたの事業の本質に集中できる時間を取り戻します。
          </p>
        </div>
        <div className="relative text-xs text-emerald-200">© 2026 PRC会計 SaaS</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            data-testid="login-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> ホームに戻る
          </Link>

          <h1 className="font-display font-bold text-3xl text-stone-900">おかえりなさい</h1>
          <p className="mt-2 text-sm text-stone-500">アカウント情報を入力してログインしてください。</p>

          {twoFA ? (
            <form onSubmit={handleVerify} className="mt-8 space-y-5" data-testid="twofa-form">
              {error && (
                <div data-testid="login-error" className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700">
                  {error}
                </div>
              )}
              <p className="text-sm text-stone-600">認証アプリに表示されている6桁のコードを入力してください。</p>
              <div>
                <Label htmlFor="code" className="text-stone-700 text-sm">認証コード</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="twofa-code-input"
                  className="mt-1.5 tracking-[0.4em] text-center text-lg"
                  placeholder="000000"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="twofa-verify-button"
                className="w-full inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950 transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "確認してログイン"}
              </button>
              <button
                type="button"
                onClick={() => { setTwoFA(null); setCode(""); setError(""); }}
                data-testid="twofa-back-button"
                className="w-full text-sm text-stone-500 hover:text-stone-900"
              >
                戻る
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            {error && (
              <div
                data-testid="login-error"
                className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700"
              >
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-stone-700 text-sm">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-700 text-sm">パスワード</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950 transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "ログイン"}
            </button>
          </form>

          <p className="mt-6 text-sm text-stone-500">
            アカウントをお持ちでない方は{" "}
            <Link
              to="/register"
              data-testid="login-register-link"
              className="text-emerald-800 font-medium link-underline"
            >
              新規登録
            </Link>
          </p>

          <div className="mt-8 space-y-2">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">ログイン情報（クリックで自動入力）</div>
            <button
              type="button"
              data-testid="platform-admin-fill-button"
              onClick={() => {
                setEmail("admin@kessan.jp");
                setPassword("admin123");
                setError("");
              }}
              className="w-full text-left rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-stone-600 hover:border-emerald-400 hover:bg-emerald-100 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-emerald-900">運営会社アカウント（プラットフォーム管理者）</span>
                <span className="text-emerald-700 font-medium">自動入力</span>
              </div>
              <div>メール: admin@kessan.jp / パスワード: admin123</div>
              <div className="mt-0.5 text-[11px] text-emerald-700">全社を横断管理できる運営者権限</div>
            </button>
            <button
              type="button"
              data-testid="demo-user-fill-button"
              onClick={() => {
                setEmail("demo@kessan.jp");
                setPassword("demo123");
                setError("");
              }}
              className="w-full text-left rounded-md border border-stone-200 bg-stone-100 px-4 py-3 text-xs text-stone-600 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-stone-700">会計管理者アカウント</span>
                <span className="text-emerald-700 font-medium">自動入力</span>
              </div>
              <div>メール: demo@kessan.jp / パスワード: demo123</div>
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
